// src/contexts/AdminAuthContext.tsx
import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { supabase } from '@/integrations/supabase/client';
import bcrypt from 'bcryptjs';

import { logger } from '@/services/logger';
interface Admin {
  id: string;
  email: string;
  name: string;
  role?: string;  // 'Super Admin' | 'Account Manager' | 'Viewer'
  permissions?: {
    [feature: string]: string[];
  };
}

interface AdminAuthContextType {
  admin: Admin | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  login: (email: string, password: string) => Promise<void>;
  logout: () => void;
}

const AdminAuthContext = createContext<AdminAuthContextType | undefined>(undefined);

const TOKEN_STORAGE_KEY = 'admin-token';

const decodeJwt = (token: string) => {
  try {
    const base64Url = token.split('.')[1];
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    const jsonPayload = decodeURIComponent(atob(base64).split('').map(c => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2)).join(''));
    return JSON.parse(jsonPayload);
  } catch (e) { return null; }
};

const isTokenExpired = (token: string): boolean => {
  const payload = decodeJwt(token);
  return !payload || !payload.exp || Date.now() >= payload.exp * 1000;
};

export const AdminAuthProvider = ({ children }: { children: ReactNode }) => {
  const [admin, setAdmin] = useState<Admin | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const initializeAuth = () => {
      try {
        const storedToken = localStorage.getItem(TOKEN_STORAGE_KEY);
        if (storedToken && !isTokenExpired(storedToken)) {
          const payload = decodeJwt(storedToken);
          setAdmin({ 
            id: payload.sub, 
            email: payload.email, 
            name: payload.name,
            role: payload.actualRole || payload.role, // Use actualRole if available
            permissions: payload.permissions
          });
        }
      } catch (error) {
        logger.error('Failed to initialize auth from storage', error);
        setAdmin(null);
        localStorage.removeItem(TOKEN_STORAGE_KEY);
      } finally {
        setIsLoading(false);
      }
    };
    initializeAuth();
  }, []);

  const login = async (email: string, password: string) => {
    // 1. Fetch user data (including hash) from server
    const { data: adminUser, error: userError } = await supabase.functions.invoke('admin-get-user', {
      body: { email },
    });

    if (userError || !adminUser) {
      throw new Error('Invalid email or password');
    }

    if (!adminUser.is_active) {
      throw new Error('Account is inactive. Please contact support.');
    }

    // 2. Verify password on the client-side
    const isPasswordValid = await bcrypt.compare(password, adminUser.password_hash);
    if (!isPasswordValid) {
      throw new Error('Invalid email or password');
    }

    // 3. If password is valid, issue a JWT from the server
    const { data: tokenData, error: tokenError } = await supabase.functions.invoke('admin-issue-jwt', {
      body: { userId: adminUser.id, email: adminUser.email, name: adminUser.name },
    });

    if (tokenError || !tokenData.token) {
      throw new Error('Failed to issue session token.');
    }

    const newToken = tokenData.token;
    if (newToken && !isTokenExpired(newToken)) {
      const payload = decodeJwt(newToken);
      setAdmin({ 
        id: payload.sub, 
        email: payload.email, 
        name: payload.name,
        role: payload.actualRole || payload.role, // Use actualRole if available
        permissions: payload.permissions
      });
      localStorage.setItem(TOKEN_STORAGE_KEY, newToken);
    } else {
      throw new Error('Invalid token received');
    }
  };

  const logout = () => {
    setAdmin(null);
    localStorage.removeItem(TOKEN_STORAGE_KEY);
  };

  const value = {
    admin,
    isAuthenticated: !!admin,
    isLoading,
    login,
    logout,
  };

  return (
    <AdminAuthContext.Provider value={value}>
      {children}
    </AdminAuthContext.Provider>
  );
};

export const useAdminAuth = () => {
  const context = useContext(AdminAuthContext);
  if (context === undefined) {
    throw new Error('useAdminAuth must be used within an AdminAuthProvider');
  }
  return context;
};
